import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Autocomplete,
  Button,
  Grid,
  Select,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import DetailsModal from '@/components/DetailsModal';
import expenseTypeQuery from '@/api/queries/expenseType';
import validations from '@/helpers/validation';
import { DatePickerInput } from '@mantine/dates';
import agentMutate from '@/api/mutitations/agent';
import s3Mutations from '@/api/mutitations/s3';
import { YES_NO } from '@/helpers/enums';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';

function AgentExpensesCreate(props) {
  const {
    open,
    close,
    opened,
    setKey,
    id,
  } = props;

  const t = prepareTranslate();
  const { useUpload } = s3Mutations;
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();
  const { data: expenseTypeData } = expenseTypeQuery.useGetAllExpenseTypes();

  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      files: [],
      isRecurring: 'no',
      isPaid: 'no',
    },
    validate: {
      value: (value) => validations.required(value),
      type: (value) => validations.required(value),
      date: (value, values) => (
        values.isRecurring === 'yes' ? null : validations.required(value)
      ),
    },
  });

  const isRecurring = form.values.isRecurring === 'yes';

  const { mutateAsync: addExpensesMutate } = agentMutate.usePushToArray();

  const handleExpensesSave = async (values) => {
    setLoading(true);
    let uploadFiles;

    try {
      const clonedValues = { ...values };
      const { files } = values;

      if (files.length > 0) {
        const formData = new FormData();

        files.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          uploadFiles = await uploadMutate(formData);
          clonedValues.files = uploadFiles.files;
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPaid = clonedValues.isPaid === 'yes';
      clonedValues.isRecurring = clonedValues.isRecurring === 'yes';
      if (isRecurring) {
        clonedValues.isRecurringActive = clonedValues.isRecurringActive === 'yes';
      }

      const data = {
        value: { ...clonedValues },
        id,
        key: 'expenses',
      };

      const newExpenses = await addExpensesMutate(data);

      if (!newExpenses) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('components.createAgentExpense.sucess'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.agentEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleExpensesSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='clseButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  const handleChangeIsRecurring = (value) => {
    form.setFieldValue('isRecurring', value);
    if (value === 'no') {
      form.setFieldValue('isRecurringActive', null);
      form.setFieldValue('isPaid', 'no');
    } else {
      form.setFieldValue('isPaid', null);
      form.setFieldValue('payDate', null);
      form.setFieldValue('date', null);
      form.setFieldValue('isRecurringActive', 'yes');
    }
  };

  return (
    <>
      <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened={opened}
        label={t('components.createAgentExpense.title')}
      >
        <Grid gutter={{ base: 'xl' }}>
          <Grid.Col span={{ base: 3 }}>
            <Autocomplete
              label={t('components.createAgentExpense.type')}
              data={expenseTypeData}
              required
              limit="10"
              key={form.key('type')}
              {...form.getInputProps('type')}
            />
            {!isRecurring ? (
              <Select
                label={t('components.createAgentExpense.isPaid')}
                data={YES_NO}
                required
                key={form.key('isPaid')}
                {...form.getInputProps('isPaid')}
              />
            ) : (
              <Select
                label={t('components.createAgentExpense.isRecurringActive')}
                data={YES_NO}
                key={form.key('isRecurringActive')}
                {...form.getInputProps('isRecurringActive')}
                defaultValue="yes"
              />)}
          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            <TextInput
              type='number'
              autoComplete='off'
              label={t('components.createAgentExpense.sum')}
              key={form.key('sum')}
              maxLength={7}
              required
              {...form.getInputProps('value')}
            />
            {!isRecurring && (
              <DatePickerInput
                valueFormat="DD/MM/YYYY"
                key={'payDate'}
                label={t('components.createAgentExpense.payDate')}
                {...form.getInputProps('payDate')}
              />
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            {!isRecurring && (
              <DatePickerInput
                required
                valueFormat="DD/MM/YYYY"
                key={'date'}
                label={t('components.createAgentExpense.date')}
                {...form.getInputProps('date')}
              />
            )}
            <TextInput
              key={'comments'}
              label={t('components.createAgentExpense.comments')}
              {...form.getInputProps('comments')}
            />

          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            <Select
              label={t('components.createAgentExpense.isRecurring')}
              data={YES_NO}
              key={form.key('isRecurring')}
              {...form.getInputProps('isRecurring')}
              defaultValue="no"
              onChange={handleChangeIsRecurring}
            />
          </Grid.Col>
        </Grid>

        {form.values.files?.length > 0 && (
          <EditFiles
            files={form.values.files}
            onNameChange={onFileNameChange}
            onRemove={onDeleteFile}
          />
        )}
        <UploadFilesButton
          onFilesSelected={onFileSelected}
          label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
        />
      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

AgentExpensesCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  id: PropTypes.string,
};

export default AgentExpensesCreate;
